import { Injectable, ErrorHandler } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastController, AlertController } from '@ionic/angular';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { NetworkDetector } from './network-detector.service';

export interface CatchitError {
  id: string;
  timestamp: Date;
  type: 'http' | 'javascript' | 'user' | 'network' | 'storage';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  details?: any;
  userMessage?: string;
  recoverable?: boolean;
  context?: {
    url?: string;
    userId?: string;
    action?: string;
    component?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CatchitErrorHandlerService implements ErrorHandler {
  private errorLog: CatchitError[] = [];
  private readonly MAX_ERROR_LOG_SIZE = 100;

  constructor(
    private toastCtrl: ToastController,
    private alertCtrl: AlertController,
    private router: Router,
    private networkDetector: NetworkDetector
  ) {}

  /**
   * Global error handler implementation
   */
  handleError(error: any): void {
    console.error('Global error caught:', error);

    // NG0100 (ExpressionChangedAfterItHasBeenCheckedError) is an Angular dev-mode-only
    // internal consistency check - Angular disables it entirely in production builds, so
    // it can never represent a real user-facing failure. Still log it above for visibility
    // during development, but don't alarm users with the generic error alert for it.
    if (typeof error?.message === 'string' && error.message.includes('NG0100')) {
      return;
    }

    const catchitError = this.createError({
      type: 'javascript',
      severity: 'high',
      message: error.message || 'An unexpected error occurred',
      details: {
        stack: error.stack,
        name: error.name,
        originalError: error
      },
      userMessage: 'Something went wrong. Please try again or contact support if the problem persists.',
      recoverable: true
    });

    this.logError(catchitError);
    void this.showUserError(catchitError);
  }

  /**
   * Handle HTTP errors specifically
   */
  handleHttpError(error: HttpErrorResponse, context?: any): Observable<any> {
    let catchitError: CatchitError;

    switch (error.status) {
      case 0:
        // Network error
        catchitError = this.createError({
          type: 'network',
          severity: 'medium',
          message: 'Network connection error',
          details: error,
          userMessage: 'Unable to connect to the server. Please check your internet connection.',
          recoverable: true,
          context
        });
        break;

      case 400:
        // Bad Request
        catchitError = this.createError({
          type: 'http',
          severity: 'medium',
          message: 'Bad request',
          details: error,
          userMessage: 'Invalid request. Please check your input and try again.',
          recoverable: true,
          context
        });
        break;

      case 401:
        // Unauthorized
        catchitError = this.createError({
          type: 'http',
          severity: 'high',
          message: 'Unauthorized access',
          details: error,
          userMessage: 'Your session has expired. Please log in again.',
          recoverable: true,
          context
        });
        this.handleAuthenticationError();
        break;

      case 403:
        // Forbidden
        catchitError = this.createError({
          type: 'http',
          severity: 'high',
          message: 'Access forbidden',
          details: error,
          userMessage: 'You do not have permission to perform this action.',
          recoverable: false,
          context
        });
        break;

      case 404:
        // Not Found
        catchitError = this.createError({
          type: 'http',
          severity: 'medium',
          message: 'Resource not found',
          details: error,
          userMessage: 'The requested resource was not found.',
          recoverable: true,
          context
        });
        break;

      case 422:
        // Validation Error
        catchitError = this.createError({
          type: 'http',
          severity: 'medium',
          message: 'Validation error',
          details: error,
          userMessage: this.extractValidationMessage(error),
          recoverable: true,
          context
        });
        break;

      case 500:
        // Internal Server Error
        catchitError = this.createError({
          type: 'http',
          severity: 'critical',
          message: 'Internal server error',
          details: error,
          userMessage: 'A server error occurred. Please try again later or contact support.',
          recoverable: true,
          context
        });
        break;

      case 503:
        // Service Unavailable
        catchitError = this.createError({
          type: 'http',
          severity: 'high',
          message: 'Service unavailable',
          details: error,
          userMessage: 'The service is temporarily unavailable. Please try again later.',
          recoverable: true,
          context
        });
        break;

      default:
        // Generic HTTP error
        catchitError = this.createError({
          type: 'http',
          severity: 'medium',
          message: `HTTP ${error.status} error`,
          details: error,
          userMessage: `An error occurred (${error.status}). Please try again.`,
          recoverable: true,
          context
        });
        break;
    }

    this.logError(catchitError);

    // The warning triangle in the header (NetworkDetector + HomePage) already communicates
    // offline status persistently - showing a toast/alert on top of every single request
    // that fails while offline is redundant, and per Giles's report, wrong: the app should
    // go offline silently, not pop an error for it. Originally scoped to status 0 only
    // (network-unreachable), but confirmed live that being offline can also surface as a
    // genuine timeout status (504) rather than an instant connection failure - the request
    // did actually reach some intermediate layer before failing. So this now suppresses
    // based on NetworkDetector's confirmed offline state alone, any status - a real
    // server-side error (400/401/500 etc.) while online is unaffected, since this only
    // fires when NetworkDetector independently agrees we're offline.
    const isSilentOfflineNetworkError = this.networkDetector.getStatus()?.connected === false;
    if (!isSilentOfflineNetworkError) {
      void this.showUserError(catchitError);
    }

    // Return empty observable to prevent further error propagation
    return of(null);
  }

  /**
   * Handle user-initiated errors (validation, user mistakes, etc.)
   */
  handleUserError(message: string, details?: any, context?: any): void {
    const catchitError = this.createError({
      type: 'user',
      severity: 'low',
      message,
      details,
      userMessage: message,
      recoverable: true,
      context
    });

    this.logError(catchitError);
    void this.showUserError(catchitError, 'toast');
  }

  /**
   * Handle storage errors (offline, quota exceeded, etc.)
   */
  handleStorageError(error: any, context?: any): void {
    let userMessage = 'Storage error occurred.';

    if (error.name === 'QuotaExceededError') {
      userMessage = 'Storage quota exceeded. Please free up space or contact support.';
    } else if (error.name === 'NotAllowedError') {
      userMessage = 'Storage access denied. Please check your browser settings.';
    }

    const catchitError = this.createError({
      type: 'storage',
      severity: 'medium',
      message: error.message || 'Storage operation failed',
      details: error,
      userMessage,
      recoverable: true,
      context
    });

    this.logError(catchitError);
    void this.showUserError(catchitError);
  }

  /**
   * Create a standardized error object
   */
  private createError(params: {
    type: CatchitError['type'];
    severity: CatchitError['severity'];
    message: string;
    details?: any;
    userMessage?: string;
    recoverable?: boolean;
    context?: any;
  }): CatchitError {
    return {
      id: this.generateErrorId(),
      timestamp: new Date(),
      type: params.type,
      severity: params.severity,
      message: params.message,
      details: params.details,
      userMessage: params.userMessage || params.message,
      recoverable: params.recoverable ?? true,
      context: {
        url: window.location.href,
        userId: this.getCurrentUserId(),
        ...params.context
      }
    };
  }

  /**
   * Log error to internal storage and optionally send to server
   */
  private logError(error: CatchitError): void {
    // Add to internal log
    this.errorLog.unshift(error);

    // Keep log size manageable
    if (this.errorLog.length > this.MAX_ERROR_LOG_SIZE) {
      this.errorLog.splice(this.MAX_ERROR_LOG_SIZE);
    }

    // Log to console in development
    if (!window.location.hostname.includes('production')) {
      console.group(`🚨 CatchIT Error [${error.severity.toUpperCase()}]`);
      console.error(`Type: ${error.type}`);
      console.error(`Message: ${error.message}`);
      console.error(`User Message: ${error.userMessage}`);
      console.error(`Context:`, error.context);
      console.error(`Details:`, error.details);
      console.groupEnd();
    }

    // Send critical errors to monitoring service (implement as needed)
    if (error.severity === 'critical') {
      this.sendErrorToMonitoring(error);
    }
  }

  /**
   * Show error message to user
   */
  private async showUserError(error: CatchitError, preferredMethod?: 'toast' | 'alert'): Promise<void> {
    if (!error.userMessage) return;

    const method = preferredMethod || (error.severity === 'low' ? 'toast' : 'alert');

    if (method === 'toast') {
      const toast = await this.toastCtrl.create({
        message: error.userMessage,
        duration: error.severity === 'low' ? 3000 : 5000,
        color: this.getErrorColor(error.severity),
        position: 'top',
        buttons: error.recoverable ? [
          {
            text: 'Dismiss',
            role: 'cancel'
          }
        ] : undefined
      });
      await toast.present();
    } else {
      const alert = await this.alertCtrl.create({
        header: this.getErrorTitle(error.severity),
        message: error.userMessage,
        buttons: [
          {
            text: 'OK',
            role: 'cancel'
          },
          ...(error.recoverable ? [
            {
              text: 'Report Issue',
              handler: () => this.reportError(error)
            }
          ] : [])
        ]
      });
      await alert.present();
    }
  }

  /**
   * Handle authentication errors by redirecting to login
   */
  private handleAuthenticationError(): void {
    // Clear any stored authentication data
    localStorage.removeItem('authToken');

    // Redirect to home/login page
    void this.router.navigate(['/'], { replaceUrl: true });
  }

  /**
   * Extract validation messages from HTTP error response
   */
  private extractValidationMessage(error: HttpErrorResponse): string {
    if (error.error?.message) {
      return error.error.message;
    }

    if (error.error?.errors) {
      const messages = Object.values(error.error.errors).flat();
      return messages.join('. ');
    }

    return 'Please check your input and try again.';
  }

  /**
   * Generate unique error ID
   */
  private generateErrorId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current user ID from authentication service
   */
  private getCurrentUserId(): string | undefined {
    // In a real implementation, get this from your auth service
    return 'current-user-id';
  }

  /**
   * Get error color based on severity
   */
  private getErrorColor(severity: CatchitError['severity']): string {
    const colorMap = {
      low: 'primary',
      medium: 'warning',
      high: 'danger',
      critical: 'danger'
    };
    return colorMap[severity];
  }

  /**
   * Get error title based on severity
   */
  private getErrorTitle(severity: CatchitError['severity']): string {
    const titleMap = {
      low: 'Notice',
      medium: 'Warning',
      high: 'Error',
      critical: 'Critical Error'
    };
    return titleMap[severity];
  }

  /**
   * Send error to monitoring service
   */
  private sendErrorToMonitoring(error: CatchitError): void {
    // Implement error monitoring integration here
    // Could send to services like Sentry, LogRocket, etc.
    console.warn('Critical error logged for monitoring:', error);
  }

  /**
   * Report error to support
   */
  private reportError(error: CatchitError): void {
    // Implement error reporting functionality
    // Could open email client, send to support API, etc.
    console.warn('Error reported:', error);
  }

  /**
   * Public methods for getting error information
   */
  getErrorLog(): CatchitError[] {
    return [...this.errorLog];
  }

  getRecentErrors(count: number = 10): CatchitError[] {
    return this.errorLog.slice(0, count);
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }

  getErrorById(id: string): CatchitError | undefined {
    return this.errorLog.find(error => error.id === id);
  }

  /**
   * Utility method for components to show success messages
   */
  async showSuccessMessage(message: string): Promise<void> {
    const toast = await this.toastCtrl.create({
      message,
      duration: 3000,
      color: 'success',
      position: 'top',
      buttons: [
        {
          text: 'Dismiss',
          role: 'cancel'
        }
      ]
    });
    await toast.present();
  }

  /**
   * Utility method for components to show info messages
   */
  async showInfoMessage(message: string): Promise<void> {
    const toast = await this.toastCtrl.create({
      message,
      duration: 4000,
      color: 'tertiary',
      position: 'top',
      buttons: [
        {
          text: 'Dismiss',
          role: 'cancel'
        }
      ]
    });
    await toast.present();
  }
}